/**
 * Utility functions for Azure Referral Form
 */
import { HelpFromMicrosoft } from './types'

export const NO_HELP_REQUIRED = 'No help required at this time'

export const isMicrosoftHelpRequested = (typeOfHelp?: string) =>
  Boolean(typeOfHelp) && typeOfHelp !== NO_HELP_REQUIRED

/**
 * Deal types driven by seller visibility when no Microsoft help is requested.
 * Values match Deal Details select options (display labels).
 */
export type NoHelpDealType = 'Private' | 'Partner-led'

export const getDealTypeForSellerDealView = (
  sellerDealView: NonNullable<HelpFromMicrosoft['sellerDealView']>
): NoHelpDealType => (sellerDealView === 'YES' ? 'Partner-led' : 'Private')

const emptyHelpContactFields: Pick<
  HelpFromMicrosoft,
  'firstName' | 'lastName' | 'phone' | 'email'
> = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
}

/**
 * Updates Help from Microsoft when type of help changes.
 * - Help requested: keep contacts; clear seller visibility (follow-up is hidden).
 * - No help: clear contacts; default seller visibility to YES (Partner-led).
 */
export const getHelpFromMicrosoftForTypeOfHelp = ({
  helpFromMicrosoft,
  typeOfHelp,
}: {
  helpFromMicrosoft: HelpFromMicrosoft
  typeOfHelp: HelpFromMicrosoft['typeOfHelp']
}): HelpFromMicrosoft => {
  if (isMicrosoftHelpRequested(typeOfHelp)) {
    return {
      ...helpFromMicrosoft,
      typeOfHelp,
      sellerDealView: undefined,
    }
  }

  return {
    ...helpFromMicrosoft,
    typeOfHelp,
    ...emptyHelpContactFields,
    // PRD: default Yes → Partner-led; user can switch to No → Private
    sellerDealView: helpFromMicrosoft.sellerDealView ?? 'YES',
  }
}

export const isFormEditable = (
  status?: string,
  substatus?: string
): boolean => {
  if (!status) {
    // If no status is set, allow editing (new referral)
    return true
  }

  const normalizedStatus = status.toLowerCase()
  const normalizedSubstatus = substatus?.toLowerCase() || ''

  // Terminal substatus values - always read-only
  const terminalSubstatuses = ['won', 'lost', 'declined', 'expired']
  if (terminalSubstatuses.includes(normalizedSubstatus)) {
    return false
  }

  // Closed status - always read-only
  if (normalizedStatus === 'closed') {
    return false
  }

  if (normalizedStatus === 'new' && normalizedSubstatus === 'received') {
    return true
  }

  if (normalizedStatus === 'active' && normalizedSubstatus === 'accepted') {
    return true
  }

  // All other combinations - read-only
  return false
}

/**
 * Determines if a specific field is editable based on status and field name
 */
export const isFieldEditable = (
  status?: string,
  substatus?: string,
  fieldName?: string
): boolean => {
  // If form is not editable at all, no fields are editable
  if (!isFormEditable(status, substatus)) {
    return false
  }

  // If no field name provided, use general form editability
  if (!fieldName) {
    return true
  }

  // List of always-editable fields when form is editable
  const editableFields = [
    'dealName',
    'estimatedCloseDate',
    'estimatedValue',
    'partnerNotes',
    'notesToMicrosoft',
    'crmId',
    'marketingCampaignID',
    'teamMembers',
    'contactDetails',
    'solutionsOffered',
    'companySolutions',
    'microsoftSolutions',
    'selectedSolutions',
  ]

  return editableFields.includes(fieldName)
}

/**
 * Gets a user-friendly message explaining why the form is read-only*
 */
export const getReadOnlyMessage = (
  status?: string,
  substatus?: string
): string => {
  if (isFormEditable(status, substatus)) {
    return ''
  }

  const normalizedStatus = status?.toLowerCase() || ''
  const normalizedSubstatus = substatus?.toLowerCase() || ''

  // Terminal substatus messages
  if (normalizedSubstatus === 'won') {
    return 'This referral has been won and cannot be edited.'
  }

  if (normalizedSubstatus === 'lost') {
    return 'This referral has been lost and cannot be edited.'
  }

  if (normalizedSubstatus === 'declined') {
    return 'This referral has been declined and cannot be edited.'
  }

  if (normalizedSubstatus === 'expired') {
    return 'This referral has expired and cannot be edited. Referrals automatically expire after 14 days of inactivity.'
  }

  if (normalizedStatus === 'closed') {
    return `This referral is closed and cannot be edited.`
  }

  return 'This referral is not in an editable state.'
}
